import './styles.scss';

import { Col, ListGroup, Row } from 'react-bootstrap';
import { ReactElement, useEffect, useState } from 'react';
import { Dayts, DaytsFormatEnum, createNotification, hasWhitespace, isNil, preventDefault } from '@/utils';

import CompleteEvaluationButton from '@/pages/Users/components/CompletionEvaluationButton';
import Form from '@/common/Form';
import HasPermission from '@/components/HasPermission';
import LicensesForm from '@/pages/Users/components/UserModal/LicensesForm';
import { LoadingBlur } from '@/components/LoadingSpinner';
import Modal from '@/common/Modal';
import Tippy from '@tippyjs/react';
import { Toast } from '@/models';
import TrainingChecklistTable from '@/pages/Users/components/TrainingChecklistTable';
import { User } from '@/models/gen/graphql';
import UserAvatar from '@/pages/Users/components/UserModal/UserAvatar';
import UserInfo from '@/pages/Users/components/UserModal/UserInfo';
import { UserModalTypes } from '@/pages/Users/components/UserModal/types';
import UserProfileForm from '@/pages/Users/components/UserModal/UserProfileForm';
import UserTitle from '@/pages/Users/components/UserModal/UserTitle';
import createOrUpdateUser from '@/pages/Users/api/services/createOrUpdateUser';
import { formatPhoneNumber } from '@/common/PhoneInput/utils';
import { isUsernameTaken, refetchSearchUser } from '@/pages/Users/api/services/searchUsers';
import useConfirmation from '@/hooks/useConfirmation';
import { useFormState } from 'react-hook-form';
import useSession from '@/state/session';

const DirtyTracker = ({ onChange }: { onChange: (dirty: boolean) => void }): null => {
  const { isDirty } = useFormState();
  useEffect(() => onChange(isDirty), [isDirty, onChange]);
  return null;
};

const UserModal = ({ userIds, onSubmit, onHide, ...modalProps }: UserModalTypes.Props): ReactElement => {
  const driverRoleId = useSession(({ state }) => state.config.driverRoleId);
  const [activeTab, setActiveTab] = useState<UserModalTypes.Tabs | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [_diffUsers, setDiffUsers] = useState<User[]>([]);
  const [isDriver, setIsDriver] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [checkedUsername, setCheckedUsername] = useState<{ username: string; taken: boolean } | null>(null);

  const confirmDiscard = useConfirmation({
    Header: { title: 'Are you sure?' },
    Body: { message: 'You have unsaved changes that will be lost. Are you sure?' },
    Footer: { resolve: 'Yes', reject: 'No' },
  });

  const handleHide = async (): Promise<void> => {
    if (dirty) {
      try {
        await confirmDiscard();
      } catch {
        return;
      }
    }
    onHide();
  };

  const getUser = async (...ids: (string | null | undefined)[]) => {
    switch (ids.at(0)) {
      // No user ID provided.
      case undefined:
        setUser(undefined);
        return;
      // Creating a new user.
      case null: {
        const today = new Dayts().formatAs(DaytsFormatEnum.DATE_INPUT);
        setUser({ dob: today, startDate: today } as User);
        return;
      }
      // Load existing user.
      default:
        try {
          setLoading(true);
          const users = await refetchSearchUser(...ids);
          if (users?.length === 0) throw new Error(`refetchSearchUser: no user found for id ${ids}`);
          const result = users.map((user) => {
            user = Object.entries(user).reduce((acc, [key, value]) => {
              acc[key] = isNil(value) ? undefined : value;
              return acc;
            }, {} as User);
            user.licenses = user.licenses || [];
            user.groups = user.groups || [];
            user.airportCodes = user.airportCodes || [];
            user.phone = user.phone ? formatPhoneNumber(user.phone) : '';
            return user;
          });
          const [user, ...diffs] = result;
          setUser(user);
          setDiffUsers(diffs);
          return user;
        } catch (err) {
          console.error(err.message || err);
        } finally {
          setLoading(false);
        }
    }
  };

  const handleChange = (values) => {
    if (isDriver === (values.roleId === driverRoleId)) return;
    setIsDriver(values.roleId === driverRoleId);
  };
  // Only hit the server for a new, otherwise-valid username - the Validator handles empty/spaces.
  const checkUsername = async (username: string): Promise<boolean> => {
    if (!username || hasWhitespace(username) || username === user?.username) return false;
    const taken = await isUsernameTaken(username);
    setCheckedUsername({ username, taken });
    return taken;
  };
  const handleSubmit = async (values) => {
    // Saving without leaving the username field skips the blur check, so run it here instead.
    if (checkedUsername?.username !== values.username && (await checkUsername(values.username))) return;
    try {
      setLoading(true);
      const isCreating = isNil(user?.id);
      const res = await createOrUpdateUser(userIds, values, user);
      createNotification(
        `User has been successfully ${isCreating ? 'created' : 'updated'}.`,
        Toast.Type.SUCCESS,
        isCreating ? 'User Created' : 'User Updated'
      );
      if (onSubmit) await onSubmit(res ?? values);
    } catch (err) {
      console.error(err.message || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDirty(false);
    setCheckedUsername(null);
    setActiveTab(
      HasPermission.check('allowViewUserProfileTab')
        ? UserModalTypes.Tabs.PROFILE
        : HasPermission.check('allowViewTrainingChecklist')
          ? UserModalTypes.Tabs.TRAINING
          : null
    );
    getUser(...(userIds === undefined ? [] : userIds === null ? [null] : userIds));
  }, [userIds]);

  return (
    <Modal {...modalProps} className="UserModal" show={userIds !== undefined} onHide={handleHide} size="xl">
      <LoadingBlur loading={user === null || loading} />
      {user && driverRoleId && (
        <Form
          value={user}
          onChange={handleChange}
          onSubmit={handleSubmit}
          validator={UserModalTypes.Validator(driverRoleId, checkedUsername?.taken ? checkedUsername.username : undefined)}
          key={user?.id || userIds?.at(0) || 'new'}
        >
          <DirtyTracker onChange={setDirty} />
          {user?.createdAt && (
            <Row className="UserModal-Profile">
              <Col>
                <UserAvatar userId={user?.id} src={user?.avatar} />
                <UserInfo createdAt={user?.createdAt} licenses={user?.licenses} airports={user?.airports} />
              </Col>
            </Row>
          )}
          <Row className="UserModal-Title mx-1">
            <Col className="d-flex justify-content-between align-items-center p-0">
              <Form.Watch name={['firstName', 'lastName']}>
                {([firstName, lastName]) => <UserTitle firstName={firstName} lastName={lastName} />}
              </Form.Watch>
              {UserModalTypes.Tabs.TRAINING === activeTab && (
                <div>
                  <CompleteEvaluationButton user={user} />
                </div>
              )}
            </Col>
          </Row>
          <Row className="UserModal-Tabs">
            <Col>
              <ListGroup horizontal>
                <HasPermission name="allowViewUserProfileTab">
                  <ListGroup.Item
                    action
                    active={activeTab === UserModalTypes.Tabs.PROFILE}
                    onClick={preventDefault(() => setActiveTab(UserModalTypes.Tabs.PROFILE))}
                  >
                    Profile
                  </ListGroup.Item>
                </HasPermission>
                {!isNil(user?.id) && HasPermission.check('allowViewTrainingChecklist') && (
                  <Form.Watch name="startDate">
                    {([startDate]) => (
                      <ListGroup.Item
                        action
                        active={activeTab === UserModalTypes.Tabs.TRAINING}
                        onClick={preventDefault(() => setActiveTab(UserModalTypes.Tabs.TRAINING))}
                      >
                        {!startDate && (
                          <Tippy
                            content={
                              <span>
                                <strong>Hire date required</strong> on profile to use training checklist
                              </span>
                            }
                          >
                            <span>
                              <i className="sv sv-warning" />
                            </span>
                          </Tippy>
                        )}
                        <span>Training Checklist</span>
                      </ListGroup.Item>
                    )}
                  </Form.Watch>
                )}
                <Form.Watch name={['roleId', 'licenses']}>
                  {([roleId, licenses]) =>
                    roleId === driverRoleId &&
                    HasPermission.check('allowViewUserProfileTab') && (
                      <ListGroup.Item
                        action
                        active={activeTab === UserModalTypes.Tabs.LICENSES}
                        onClick={preventDefault(() => setActiveTab(UserModalTypes.Tabs.LICENSES))}
                      >
                        {licenses?.length === 0 && (
                          <span>
                            <i className="sv sv-warning" />
                          </span>
                        )}
                        <span>Licenses</span>
                      </ListGroup.Item>
                    )
                  }
                </Form.Watch>
              </ListGroup>
            </Col>
          </Row>
          <Row className="UserModal-Content">
            <Col>
              <HasPermission name="allowViewUserProfileTab">
                <Row
                  className="UserModal-Content-Profile"
                  style={{ display: activeTab === UserModalTypes.Tabs.PROFILE ? 'block' : 'none' }}
                >
                  <Col>
                    <UserProfileForm
                      takenUsername={checkedUsername?.taken ? checkedUsername.username : undefined}
                      onUsernameBlur={checkUsername}
                    />
                  </Col>
                </Row>
              </HasPermission>
              {!isNil(user?.id) && HasPermission.check('allowViewTrainingChecklist') && (
                <Row
                  className="UserModal-Content-Training"
                  style={{ display: activeTab === UserModalTypes.Tabs.TRAINING ? 'block' : 'none' }}
                >
                  <Col>
                    <TrainingChecklistTable userId={user?.id} driverFirstName={user?.firstName} driverLastName={user?.lastName} />
                  </Col>
                </Row>
              )}
              <HasPermission name="allowViewUserProfileTab">
                <Row
                  className="UserModal-Content-Licenses"
                  style={{ display: activeTab === UserModalTypes.Tabs.LICENSES ? 'block' : 'none' }}
                >
                  <Col>
                    <LicensesForm />
                  </Col>
                </Row>
              </HasPermission>
            </Col>
          </Row>
          {activeTab !== UserModalTypes.Tabs.TRAINING && (
            <HasPermission name="allowViewUserProfileTab">
              <Row className="UserModal-Footer">
                <Col>
                  <Form.Button type="button" variant="secondary" onClick={handleHide}>
                    Cancel
                  </Form.Button>
                </Col>
                <Col>
                  <Form.Watch>
                    {(_, form) => (
                      <Form.Button type="submit" variant="primary" disabled={!form.formState.isValid}>
                        Save
                      </Form.Button>
                    )}
                  </Form.Watch>
                </Col>
              </Row>
            </HasPermission>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default UserModal;
